import { firstValueFrom, type Observable, timeout, TimeoutError } from 'rxjs'
import {
  Configuration,
  DocumentsApi as DocumentsApiClientV5,
  type IDocumentsApi,
} from '@taxfyle/documents-api-client/v5'
import { type ApiClientOptions, createRealtimeEvents } from '@/infra/api-client'
import { type DocumentsRealtimeMessage } from '@/features/documents/api/resources/realtime-messages'

/**
 * The v5 Documents API.
 */
export interface DocumentsApiV5 {
  /**
   * The API client.
   */
  documents: IDocumentsApi

  /**
   * Emits every realtime event on the connection.
   * Don't subscribe until there is a token.
   */
  events$: Observable<DocumentsRealtimeMessage>
}

/**
 * Creates the v5 Documents API client.
 */
export function createDocumentsApiV5(
  options: ApiClientOptions,
): DocumentsApiV5 {
  const getToken = async () => {
    try {
      return await firstValueFrom(options.token$.pipe(timeout(10_000)))
    } catch (err: unknown) {
      if (err instanceof TimeoutError) {
        throw new Error('Timed out waiting for a valid token', { cause: err })
      }
      throw err
    }
  }

  return {
    documents: new DocumentsApiClientV5(
      new Configuration({
        basePath: options.baseURL,
        // A falsy token makes the generated runtime silently omit the
        // Authorization header, so the getter must throw on failure.
        accessToken: getToken,
      }),
    ),
    events$: createRealtimeEvents(options),
  }
}
